import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentAuth } from '../contexts/AgentAuthContext';
import { ArrowLeft, Send, MessageCircle, CheckCircle, Phone } from 'lucide-react';

const AgentMessageSender = () => {
  const { agent } = useAgentAuth();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedContent, setSelectedContent] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const contentOptions = [
    {
      id: 'location',
      title: 'Location Details',
      description: 'Send location address, directions, and contact information'
    },
    {
      id: 'videos',
      title: 'Sample Videos',
      description: 'Share exciting jump videos and safety demonstrations'
    },
    {
      id: 'terms',
      title: 'Terms & Conditions',
      description: 'Important booking terms and cancellation policy'
    },
    {
      id: 'medical',
      title: 'Medical Requirements',
      description: 'Health guidelines and medical restrictions'
    },
    {
      id: 'manager',
      title: 'Site Manager Contact',
      description: 'Direct contact number for on-site assistance'
    }
  ];

  const toggleContent = (id) => {
    if (selectedContent.includes(id)) {
      setSelectedContent(selectedContent.filter(item => item !== id));
    } else {
      setSelectedContent([...selectedContent, id]);
    }
  };

  const handleSend = () => {
    // Build the message
    let message = `Hello! Thank you for choosing Indya Bungy. Here's the information you requested:\n\n`;
    
    if (selectedContent.includes('location')) {
      message += `📍 LOCATION:\nDelhi NCR - Sector 18, Noida\nContact: +91 9876543210\n\n`;
    }
    
    if (selectedContent.includes('videos')) {
      message += `🎥 SAMPLE VIDEOS:\nWatch our jumps: https://indyabungy.com/gallery\n\n`;
    }
    
    if (selectedContent.includes('terms')) {
      message += `📋 TERMS & CONDITIONS:\nView complete terms: https://indyabungy.com/terms\n\n`;
    }
    
    if (selectedContent.includes('medical')) {
      message += `⚕️ MEDICAL REQUIREMENTS:\nAge: 12+ years, Weight: 40-120 kg\nNo heart/spine conditions\nFull details: https://indyabungy.com/medical\n\n`;
    }
    
    if (selectedContent.includes('manager')) {
      message += `👨‍💼 SITE MANAGER:\nContact: +91 9999888877\nAvailable 8 AM - 8 PM daily\n\n`;
    }
    
    if (customMessage) {
      message += `💬 ADDITIONAL MESSAGE:\n${customMessage}\n\n`;
    }
    
    message += `Best regards,\n${agent?.agencyName}\n${agent?.phone}`;

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    
    // Open in new window
    window.open(whatsappUrl, '_blank');
    
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/agent/dashboard')}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="text-white" size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Send Information</h1>
              <p className="text-gray-400 text-sm">Share location, videos, T&C, and more via WhatsApp</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          {success && (
            <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-lg p-4 flex items-center space-x-3">
              <CheckCircle className="text-green-400" size={24} />
              <span className="text-green-400 font-medium">WhatsApp opened! Message ready to send.</span>
            </div>
          )}

          {/* Customer Phone Number */}
          <div className="mb-6">
            <label className="text-white font-medium mb-2 block">Customer WhatsApp Number *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+91 9876543210"
                className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              />
            </div>
          </div>

          {/* Content Selection */}
          <div className="mb-6">
            <label className="text-white font-medium mb-3 block">Select Information to Share *</label>
            <div className="space-y-3">
              {contentOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => toggleContent(option.id)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedContent.includes(option.id)
                      ? 'border-yellow-400 bg-yellow-400/10'
                      : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedContent.includes(option.id)
                        ? 'border-yellow-400 bg-yellow-400'
                        : 'border-gray-500'
                    }`}>
                      {selectedContent.includes(option.id) && (
                        <CheckCircle className="text-black" size={16} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-bold">{option.title}</h4>
                      <p className="text-gray-400 text-sm mt-1">{option.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Message */}
          <div className="mb-6">
            <label className="text-white font-medium mb-2 block">Additional Custom Message (Optional)</label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={4}
              placeholder="Add any personalized message for the customer..."
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
            />
          </div>

          {/* Preview */}
          {selectedContent.length > 0 && (
            <div className="mb-6 bg-gray-900 border border-gray-600 rounded-lg p-4">
              <h4 className="text-white font-bold mb-2 flex items-center">
                <MessageCircle className="mr-2 text-yellow-400" size={20} />
                Message Preview
              </h4>
              <div className="text-gray-400 text-sm">
                <p>Customer will receive information about:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {selectedContent.map((id) => {
                    const option = contentOptions.find(opt => opt.id === id);
                    return <li key={id}>{option?.title}</li>;
                  })}
                </ul>
                {customMessage && <p className="mt-2">+ Your custom message</p>}
              </div>
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={!phoneNumber || selectedContent.length === 0}
            className="w-full flex items-center justify-center space-x-2 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-lg rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MessageCircle size={24} />
            <span>Send via WhatsApp</span>
          </button>

          <p className="text-gray-500 text-xs text-center mt-4">
            This will open WhatsApp with a pre-filled message. You can review and edit before sending.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentMessageSender;